import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Sparkles, Loader2, Plus, X } from "lucide-react";
import { Layout } from "@/components/Layout";
import { api } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";
import { toast } from "sonner";

const inputCls = "w-full rounded-xl border border-slate-200 px-4 py-2.5 text-sm focus:border-brand-blue focus:outline-none focus:ring-2 focus:ring-brand-blue/20";
const Field = ({ label, children }) => (<div><label className="text-sm font-semibold text-navy">{label}</label><div className="mt-1">{children}</div></div>);

export default function PostJob() {
  const { t } = useLang();
  const nav = useNavigate();
  const [emp, setEmp] = useState(null);
  const [f, setF] = useState({
    title: "", description: "", city: "", country: "India", work_mode: "on-site",
    employment_type: "full-time", salary_min: "", salary_max: "", currency: "INR",
    skills: "", experience_required: "", openings: 1, weeks: 4, extraCities: "",
  });
  const [quote, setQuote] = useState(null);
  const [aiBusy, setAiBusy] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => { api.get("/employer/me").then((r) => setEmp(r.data)).catch(() => {}); }, []);

  const upd = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const cityCount = () => 1 + f.extraCities.split(",").map((c) => c.trim()).filter(Boolean).length;

  const freeAvailable = emp && emp.free_credits > 0;

  useEffect(() => {
    if (freeAvailable) { setQuote(null); return; }
    api.get("/jobs/quote", { params: { weeks: f.weeks, cities: cityCount(), country: f.country } })
      .then((r) => setQuote(r.data)).catch(() => {});
    // eslint-disable-next-line
  }, [f.weeks, f.extraCities, f.country, emp]);

  const genAI = async () => {
    if (!f.title) { toast.error("Enter a job title first"); return; }
    setAiBusy(true);
    try {
      const { data } = await api.post("/ai/job-description", {
        title: f.title, company: emp?.company_name || "", skills: f.skills, location: f.city, extra: "",
      });
      setF((cur) => ({ ...cur, description: data.description }));
      toast.success("AI description generated");
    } catch { toast.error("AI generation failed"); }
    finally { setAiBusy(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = {
        title: f.title, description: f.description, city: f.city, country: f.country,
        work_mode: f.work_mode, employment_type: f.employment_type,
        salary_min: f.salary_min ? Number(f.salary_min) : null,
        salary_max: f.salary_max ? Number(f.salary_max) : null,
        currency: f.currency,
        skills: f.skills.split(",").map((s) => s.trim()).filter(Boolean),
        experience_required: f.experience_required, openings: Number(f.openings) || 1,
        weeks: Number(f.weeks), cities: f.extraCities.split(",").map((c) => c.trim()).filter(Boolean),
      };
      await api.post("/jobs", payload);
      toast.success(freeAvailable ? "Job posted using a free credit!" : "Job posted (payment mocked)!");
      nav("/employer");
    } catch (e2) { toast.error(e2.response?.data?.detail || "Could not post job"); }
    finally { setBusy(false); }
  };

  return (
    <Layout>
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <h1 className="font-display text-3xl font-extrabold text-navy">Post a Job</h1>
        {emp && (
          <p className="mt-2 text-sm">
            {freeAvailable
              ? <span className="font-semibold text-emerald-600" data-testid="free-credit-note">Free credits: {emp.free_credits}/5 — this post is FREE (30-day validity).</span>
              : <span className="font-semibold text-brand-orange">No free credits left — paid posting applies.</span>}
          </p>
        )}

        <form onSubmit={submit} className="mt-6 gjt-card p-6 space-y-5">
          <Field label="Job title"><input data-testid="job-title" required className={inputCls} value={f.title} onChange={upd("title")} placeholder="Sales Executive" /></Field>

          <div>
            <div className="flex items-center justify-between">
              <label className="text-sm font-semibold text-navy">Description</label>
              <button type="button" data-testid="ai-generate-btn" onClick={genAI} disabled={aiBusy}
                className="inline-flex items-center gap-1.5 rounded-lg bg-navy px-3 py-1.5 text-xs font-bold text-white disabled:opacity-60">
                {aiBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />} AI Generate
              </button>
            </div>
            <textarea data-testid="job-description" rows={6} className={`${inputCls} mt-1`} value={f.description} onChange={upd("description")} placeholder="Describe the role, or let AI write it for you." />
          </div>

          <div className="grid gap-5 sm:grid-cols-2">
            <Field label="City"><input data-testid="job-city" required className={inputCls} value={f.city} onChange={upd("city")} /></Field>
            <Field label="Country"><input data-testid="job-country" className={inputCls} value={f.country} onChange={upd("country")} /></Field>
            <Field label="Additional cities (comma separated)"><input data-testid="job-extra-cities" className={inputCls} value={f.extraCities} onChange={upd("extraCities")} placeholder="Mumbai, Delhi" /></Field>
            <Field label="Work mode">
              <select data-testid="job-workmode" className={inputCls} value={f.work_mode} onChange={upd("work_mode")}>
                {["on-site", "remote", "hybrid"].map((o) => <option key={o} value={o}>{o}</option>)}
              </select>
            </Field>
            <Field label="Employment type">
              <select data-testid="job-type" className={inputCls} value={f.employment_type} onChange={upd("employment_type")}>
                {["full-time", "part-time", "contract", "freelance", "gig", "internship"].map((o) => <option key={o} value={o}>{o}</option>)}
              </select>
            </Field>
            <Field label="Currency">
              <select data-testid="job-currency" className={inputCls} value={f.currency} onChange={upd("currency")}>
                {["INR", "USD", "EUR", "GBP", "AED"].map((o) => <option key={o} value={o}>{o}</option>)}
              </select>
            </Field>
            <Field label="Salary min"><input data-testid="job-salary-min" type="number" className={inputCls} value={f.salary_min} onChange={upd("salary_min")} /></Field>
            <Field label="Salary max"><input data-testid="job-salary-max" type="number" className={inputCls} value={f.salary_max} onChange={upd("salary_max")} /></Field>
            <Field label="Experience required"><input data-testid="job-exp" className={inputCls} value={f.experience_required} onChange={upd("experience_required")} placeholder="2+ years" /></Field>
            <Field label="Openings"><input data-testid="job-openings" type="number" className={inputCls} value={f.openings} onChange={upd("openings")} /></Field>
          </div>

          <Field label="Skills required (comma separated)"><input data-testid="job-skills" className={inputCls} value={f.skills} onChange={upd("skills")} placeholder="Sales, CRM, Negotiation" /></Field>

          {!freeAvailable && (
            <div className="rounded-xl bg-slate-50 p-4">
              <Field label="Duration (weeks)">
                <select data-testid="job-weeks" className={inputCls} value={f.weeks} onChange={upd("weeks")}>
                  {[1, 2, 4, 8].map((w) => <option key={w} value={w}>{w} week(s)</option>)}
                </select>
              </Field>
              {quote && (
                <div data-testid="billing-quote" className="mt-3 flex items-center justify-between rounded-xl bg-white p-3">
                  <span className="text-sm text-slate-500">{quote.formula}</span>
                  <span className="font-display text-xl font-extrabold text-navy">{quote.currency === "INR" ? "₹" : "$"}{quote.amount}</span>
                </div>
              )}
              <p className="mt-2 text-xs text-slate-400">Payment is MOCKED in this demo — no real charge.</p>
            </div>
          )}

          <button type="submit" disabled={busy} data-testid="submit-job" className="gjt-btn-orange w-full rounded-xl py-3 text-sm font-bold disabled:opacity-60 flex items-center justify-center gap-2">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
            {freeAvailable ? "Post free job" : `Post job ${quote ? `(${quote.currency === "INR" ? "₹" : "$"}${quote.amount})` : ""}`}
          </button>
        </form>
      </div>
    </Layout>
  );
}
